/**
 * @file life.ts
 * @description Helpers shared by the life-layer pages (Today / Tasks / Routines).
 * Day-level logic is anchored to Europe/Warsaw to match the server side
 * (server/lib/life-dates.js): a task is "dziś" / "jutro" / "zaległe" based on
 * its calendar day in Warsaw, not in the browser's local timezone. Labels are
 * resolved through i18n (same pattern as timeAgo in format.ts) so they live in
 * the `tasks` namespace alongside the rest of the page copy.
 */

import i18n from "../i18n";
import type { LifeDomain, LifeTaskPriority } from "./api";

const WARSAW_TZ = "Europe/Warsaw";

/** 'YYYY-MM-DD' for a Date in Europe/Warsaw (en-CA formats as ISO-like). */
export function dayWarsaw(d: Date = new Date()): string {
  return new Intl.DateTimeFormat("en-CA", { timeZone: WARSAW_TZ }).format(d);
}

/** datetime-local input value → ISO string (or null when cleared/invalid). */
export function localInputToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

export interface DueLabel {
  text: string;
  overdue: boolean;
}

/**
 * Human label for a due date: "dziś HH:MM" / "jutro" / "zaległe (d.MM)" /
 * plain short date for anything further out. Returns null for no due date.
 */
export function dueLabel(dueAt: string | null): DueLabel | null {
  if (!dueAt) return null;
  const due = new Date(dueAt);
  if (Number.isNaN(due.getTime())) return null;
  const today = dayWarsaw();
  const tomorrow = dayWarsaw(new Date(Date.now() + 86_400_000));
  const dueDay = dayWarsaw(due);
  const dateShort = due.toLocaleDateString("pl-PL", {
    timeZone: WARSAW_TZ,
    day: "numeric",
    month: "2-digit",
  });
  if (dueDay < today) {
    return { text: i18n.t("tasks:dueOverdue", { date: dateShort }), overdue: true };
  }
  if (dueDay === today) {
    const time = due.toLocaleTimeString("pl-PL", {
      timeZone: WARSAW_TZ,
      hour: "2-digit",
      minute: "2-digit",
    });
    return { text: i18n.t("tasks:dueToday", { time }), overdue: false };
  }
  if (dueDay === tomorrow) {
    return { text: i18n.t("tasks:dueTomorrow"), overdue: false };
  }
  return { text: dateShort, overdue: false };
}

/** Priority → dot color (HIGH red, MEDIUM amber, LOW gray). */
export const PRIORITY_DOT: Record<LifeTaskPriority, string> = {
  HIGH: "bg-red-400",
  MEDIUM: "bg-amber-400",
  LOW: "bg-gray-500",
};

/** Quick id→domain lookup for rendering colors/names on task rows. */
export function domainMap(domains: LifeDomain[]): Map<string, LifeDomain> {
  return new Map(domains.map((d) => [d.id, d]));
}
